import { useMemo } from "react";
import { isDraftRouteId, useRouteStore } from "@/features/route/state/RouteStore.jsx";
import {
  isActiveEntity,
  isPlannedEntity,
} from "@/features/route/state/RouteState.jsx";

// Derives route-table inputs from the store's route entities:
// - activeRouteId: the active plan route (comparison baseline)
// - routeSummariesById: summary blobs by route id (joined onto plan routes)
// - routeTimestampsById: calculation timestamps by route id (freshness badge)
export default function useActiveRouteSummary(visibleRouteIds) {
  const routesById = useRouteStore().state.entities.routesById;

  return useMemo(() => {
    let activeRouteId = null;
    const routeSummariesById = {};
    const routeTimestampsById = {};

    // The store accumulates every visited vessel — limit the baseline to
    // the currently shown routes so a stale other-vessel route can't win.
    // Non-numeric selection ids (drafts, reported track) are not routes here.
    const visibleIds = (visibleRouteIds ?? [])
      .map(Number)
      .filter(Number.isFinite);
    const visible =
      (visibleRouteIds?.length ?? 0) > 0 ? new Set(visibleIds) : null;

    for (const route of Object.values(routesById ?? {})) {
      const routeId = Number(route?.id);
      if (!Number.isFinite(routeId) || isDraftRouteId(routeId)) continue;
      if (
        activeRouteId == null &&
        isPlannedEntity(route) &&
        isActiveEntity(route) &&
        (visible == null || visible.has(routeId))
      ) {
        activeRouteId = routeId;
      }
      if (route?.summary != null) {
        routeSummariesById[routeId] = route.summary;
      }
      if (route?.timestamp != null) {
        routeTimestampsById[routeId] = route.timestamp;
      }
    }

    return { activeRouteId, routeSummariesById, routeTimestampsById };
  }, [routesById, visibleRouteIds]);
}
